import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import {
  MessageSquare,
  LayoutDashboard,
  Database,
  Search,
  Zap,
  Upload,
  Mail,
  HardDrive,
  Code,
  Server,
  DatabaseZap,
  Send,
  MessageCircle,
  Hash,
  Users,
  ShieldCheck,
  Shield,
  FileText,
  Wallet,
  Building2,
  BarChart3,
  Trash2,
  Settings2,
  AlertCircle,
  Users2,
  Globe,
  GitBranch,
  Bell,
  Share2,
  Filter,
  Download,
  Mic,
  Eye,
  EyeOff,
  Clock,
  Sparkles,
  Bot,
  LayoutGrid,
  Megaphone,
  MousePointerClick,
  BellRing,
  type LucideIcon,
} from "lucide-react";

interface Feature {
  name: string;
  description: string;
  icon: LucideIcon;
  path?: string;
  adminOnly?: boolean;
  badge?: string;
}

interface FeatureGroup {
  title: string;
  description: string;
  features: Feature[];
}

const featureGroups: FeatureGroup[] = [
  {
    title: "AI Research & Chat",
    description: "Conversational AI to explore and analyze your data",
    features: [
      { name: "AI Research Chat", description: "Ask questions in natural language and get AI-powered answers from your connected data sources", icon: MessageSquare, path: "/research" },
      { name: "Multi-Source Querying", description: "Select specific data sources per conversation to target your queries", icon: Database, path: "/research" },
      { name: "Streaming Responses", description: "Real-time token-by-token AI responses with tool usage indicators", icon: Sparkles, path: "/research" },
      { name: "Follow-up Suggestions", description: "AI-generated follow-up prompts based on conversation context", icon: MessageSquare, path: "/research" },
      { name: "Conversation Bookmarks", description: "Bookmark important research chats for quick access from the home page", icon: MessageSquare, path: "/research" },
      { name: "Voice Input", description: "Speech-to-text input for hands-free research queries", icon: Mic, path: "/research" },
    ],
  },
  {
    title: "Dashboards & Reporting",
    description: "Create, share, and schedule visual dashboards and reports",
    features: [
      { name: "Dashboard Builder", description: "Create dashboards with multiple widget types — stat cards, tables, charts (bar, line, area, pie)", icon: LayoutDashboard, path: "/dashboards" },
      { name: "AI Dashboard Generation", description: "Describe what you need and AI auto-generates a complete dashboard from your data", icon: Sparkles, path: "/dashboards" },
      { name: "Dashboard Sharing", description: "Share dashboards publicly via link, with specific groups, or individual team members", icon: Share2, path: "/dashboards" },
      { name: "Dashboard Subscriptions", description: "Schedule automated dashboard email reports — daily, weekly, or custom cron", icon: Bell, path: "/dashboards" },
      { name: "Interactive Filters", description: "Auto-detected filters from SQL queries with dynamic filter controls on dashboards", icon: Filter, path: "/dashboards" },
      { name: "Reports Manager", description: "Mark queries as reports and run them with configurable filters", icon: FileText, path: "/queries" },
      { name: "Report Downloads", description: "Export query results and reports with per-user daily row limits", icon: Download, path: "/queries" },
    ],
  },
  {
    title: "Saved Queries",
    description: "Build, test, and manage SQL queries with a visual editor",
    features: [
      { name: "Query Editor", description: "Write and test SQL queries with schema browser and result preview", icon: Search, path: "/queries" },
      { name: "Schema Browser", description: "Explore database tables and columns directly in the query editor", icon: Database, path: "/queries" },
      { name: "Query Bookmarks", description: "Bookmark frequently used queries for quick access", icon: Search, path: "/queries" },
      { name: "Query Subscriptions", description: "Schedule recurring query execution and email delivery", icon: Bell, path: "/queries" },
      { name: "Query Cloning", description: "Duplicate existing queries as a starting point for new analysis", icon: Search, path: "/queries" },
    ],
  },
  {
    title: "AI Agents & Workflows",
    description: "Build automated workflows triggered by events or schedules",
    features: [
      { name: "Workflow Builder", description: "Visual node-based workflow editor with drag-and-configure steps", icon: GitBranch, path: "/aiagents" },
      { name: "NL Workflow Generation", description: "Describe your workflow in plain English and AI builds it for you", icon: Bot, path: "/aiagents" },
      { name: "HTTP Request Nodes", description: "Call external APIs as part of your workflow", icon: Globe, path: "/aiagents" },
      { name: "SQL Query Nodes", description: "Execute database queries with variable interpolation from previous steps", icon: Database, path: "/aiagents" },
      { name: "Conditional Branching", description: "Add if/else logic with multiple branches based on data conditions", icon: GitBranch, path: "/aiagents" },
      { name: "Loop Nodes", description: "Iterate over query results — execute steps once per row", icon: GitBranch, path: "/aiagents" },
      { name: "Email Nodes", description: "Send emails with dynamic content as workflow actions", icon: Mail, path: "/aiagents" },
      { name: "Workflow Scheduling", description: "Run workflows on a cron schedule — every 10 min, hourly, daily, weekly, monthly", icon: Clock, path: "/aiagents" },
    ],
  },
  {
    title: "Data Connectors",
    description: "Connect and sync data from files, databases, APIs, and messaging platforms",
    features: [
      { name: "Manual File Uploads", description: "Upload CSV, XLS, and XLSX files with automatic parsing and sync", icon: Upload, path: "/data-connectors?connector=ds-manual-uploads", adminOnly: true },
      { name: "Gmail Integration", description: "OAuth-based Gmail connection to sync email attachments as data sources", icon: Mail, path: "/data-connectors?connector=ds-gmail", adminOnly: true },
      { name: "Google Drive", description: "Connect Google Drive and approve specific files for data syncing", icon: HardDrive, path: "/data-connectors?connector=ds-google-drive", adminOnly: true },
      { name: "Database Connections", description: "Connect PostgreSQL, MySQL, ClickHouse, BigQuery, MongoDB and more with schema selection", icon: DatabaseZap, path: "/data-connectors?connector=ds-sql-db", adminOnly: true },
      { name: "MCP Servers", description: "Configure Model Context Protocol data sources with custom auth headers", icon: Server, path: "/data-connectors?connector=ds-mcp-servers", adminOnly: true },
      { name: "Custom Scripts", description: "Deploy and manage custom SQL scripts for data processing", icon: Code, path: "/data-connectors?connector=ds-scripts", adminOnly: true },
      { name: "Telegram Bot", description: "Connect a Telegram bot to receive alerts and interact with your data", icon: Send, path: "/data-connectors?connector=telegram", adminOnly: true },
      { name: "WhatsApp Bot", description: "Connect WhatsApp Business for alerts and report delivery", icon: MessageCircle, path: "/data-connectors?connector=whatsapp", adminOnly: true },
      { name: "100+ Connectors Catalog", description: "Browse and request connectors across CRM, marketing, support, e-commerce, analytics and more", icon: LayoutGrid, path: "/data-connectors?connector=all-connectors", adminOnly: true },
    ],
  },
  {
    title: "Security & Compliance",
    description: "Enterprise-grade security controls for data protection",
    features: [
      { name: "PII Masking", description: "Automatic detection and masking of emails, phone numbers, SSN, credit cards, and more in AI responses", icon: EyeOff, adminOnly: true, path: "/admin" },
      { name: "PII Unmasking Controls", description: "Hourly rate-limited unmasking with full audit trail per user", icon: Eye, adminOnly: true, path: "/admin" },
      { name: "IP Whitelisting", description: "Restrict application access to approved IP addresses and CIDR ranges", icon: Shield, adminOnly: true, path: "/admin" },
      { name: "Audit Logging", description: "Complete audit trail of all user actions — create, edit, delete, access, download", icon: FileText, adminOnly: true, path: "/admin" },
      { name: "Role-Based Access Control", description: "Four user roles — Super Admin, Admin, User, and Viewer — with granular permissions", icon: ShieldCheck, adminOnly: true, path: "/admin" },
    ],
  },
  {
    title: "User & Organization Management",
    description: "Manage teams, groups, and organizational settings",
    features: [
      { name: "User Management", description: "Invite users, assign roles (admin, viewer), toggle PII access, and set download limits", icon: Users, adminOnly: true, path: "/admin" },
      { name: "Group Management", description: "Create groups and assign members for scoped dashboard sharing and permissions", icon: Users2, adminOnly: true, path: "/admin" },
      { name: "Custom User Fields", description: "Define custom fields (text, number, date, select) for user profiles", icon: Settings2, adminOnly: true, path: "/admin" },
      { name: "Corporate Info", description: "Manage organization name, logo, API keys, and branding settings", icon: Building2, adminOnly: true, path: "/admin" },
    ],
  },
  {
    title: "Billing & Cost Tracking",
    description: "Monitor usage, costs, and manage wallet credits",
    features: [
      { name: "Cost Analysis", description: "Breakdown of AI token usage and costs per user with drill-down to details", icon: BarChart3, adminOnly: true, path: "/admin" },
      { name: "Wallet & Ledger", description: "View wallet balance and full transaction history with credit/debit tracking", icon: Wallet, adminOnly: true, path: "/admin" },
      { name: "Download Quotas", description: "Per-user daily row download limits with real-time usage tracking", icon: Download, adminOnly: true, path: "/admin" },
    ],
  },
  {
    title: "Messaging & Notifications",
    description: "Receive alerts and reports on your preferred channels",
    features: [
      { name: "Telegram Alerts", description: "Get AI-powered insights and scheduled reports delivered to Telegram", icon: Send },
      { name: "WhatsApp Alerts", description: "Receive dashboard reports and alerts directly on WhatsApp", icon: MessageCircle },
      { name: "Slack Integration", description: "Connect Slack for team-wide notifications and report delivery", icon: Hash, badge: "Coming Soon" },
      { name: "Email Reports", description: "Automated email delivery of dashboards and scheduled query reports", icon: Mail, path: "/dashboards" },
    ],
  },
  {
    title: "Data Recovery & Issue Tracking",
    description: "Recover deleted data and track reported issues",
    features: [
      { name: "Recycle Bin", description: "Restore or permanently delete dashboards, queries, and conversations", icon: Trash2, adminOnly: true, path: "/admin" },
      { name: "Issue Reporting", description: "Report bugs and feature requests directly from the app with status tracking", icon: AlertCircle },
      { name: "Issue Management", description: "Admin view of all reported issues with resolution workflow", icon: AlertCircle, adminOnly: true, path: "/admin" },
    ],
  },
];

const AdvancedFeatures = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const isAdmin = profile?.admin || profile?.super_admin;

  const totalFeatures = featureGroups.reduce((sum, g) => sum + g.features.length, 0);

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="border-b border-border px-6 py-5">
        <h1 className="text-2xl font-semibold">Advanced Features</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Explore {totalFeatures} features across {featureGroups.length} categories that power your data intelligence platform.
        </p>
      </div>

      <div className="px-6 py-6 space-y-10">
        {featureGroups.map((group) => {
          const visibleFeatures = group.features.filter(
            (f) => !f.adminOnly || isAdmin
          );
          if (visibleFeatures.length === 0) return null;

          return (
            <section key={group.title}>
              <div className="mb-4">
                <h2 className="text-lg font-semibold">{group.title}</h2>
                <p className="text-sm text-muted-foreground">{group.description}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {visibleFeatures.map((feature) => {
                  const Icon = feature.icon;
                  const isClickable = !!feature.path;

                  return (
                    <button
                      key={feature.name}
                      onClick={() => {
                        if (feature.path) navigate(feature.path);
                      }}
                      disabled={!isClickable}
                      className={`flex items-start gap-3 rounded-lg border border-border p-4 text-left transition-colors ${
                        isClickable
                          ? "hover:bg-accent hover:border-primary/30 cursor-pointer"
                          : "cursor-default"
                      }`}
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent">
                        <Icon className="h-4.5 w-4.5 text-accent-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium leading-tight">{feature.name}</span>
                          {feature.adminOnly && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0">
                              Admin
                            </Badge>
                          )}
                          {feature.badge && (
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">
                              {feature.badge}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {feature.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default AdvancedFeatures;
